// @ts-strict-ignore
import {Injectable, Injector, NgZone} from "@angular/core";
import {GetCurrentUserResponse, Permissions, SimUser} from "@models";
import {Subject} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import dayjs from "dayjs";
import {Router} from "@angular/router";
import {Translations} from "@translations";
import {NotificationService} from "src/app/common/services/notification.service";
import {SimUtilityService} from "./sim-utility.service";
import {SimUserService} from "../../../api/generated/service";
import {SimPermissionService} from "./sim-permission.service";
import {GlobalEventEmitterService} from "@event-emitter";
import {SystemHub} from "../../../api/generated/hub";
import {SimConfigurationService} from "./sim-configuration.service";

/**
 * Handles all requests and actions related to the User
 */
@Injectable({
	providedIn: "root",
})
export class SimUserExtendedService extends SimUserService {
	T = Translations;

	/**
	 * permissions of specific user {@link Permissions}
	 */
	permissions: Array<Permissions> = [];
	public userChangedEmitter = new Subject<{user: SimUser}>();

	public tokenValidTo: Date = null;
	public tokenValidToMs: number = null;
	public tokenValidToFormatted: string = null;

	/**
	 * settings to get proxy configurations {@link SimConfigurationService}
	 */
	/**
	 * Calls super Constructor {@link ApiServiceBase}
	 *
	 * @param injector
	 * @param _simPermissionService
	 * @param _simNotificationService
	 * @param _emitterService
	 * @param _translate
	 * @param _router
	 * @param _zone
	 * @param _systemHub
	 */
	constructor(
		injector: Injector,
		private _simPermissionService: SimPermissionService,
		private _simNotificationService: NotificationService,
		private _emitterService: GlobalEventEmitterService,
		private _translate: TranslateService,
		private _router: Router,
		private _zone: NgZone,
		private _systemHub: SystemHub,
		private _simConfigurationService: SimConfigurationService,
	) {
		super(injector);
	}

	/**
	 * User object {@link SimUser}
	 */
	private _user: SimUser = null;

	/**
	 * Getter Method for User object
	 *
	 * @return user object {@link SimUser}
	 */
	public get user(): SimUser {
		return this._user;
	}

	/**
	 * Reloading user object and settings user as well as permissions
	 */
	public async reload(resetWebSocket: boolean = true) {
		this._user = null;
		this.permissions = [];

		let user = await this.getCurrentUser().catch((error) => {
			this._simNotificationService.notifyError(
				this._translate.instant(this.T.SIM_SERVICE_USER_PERMISSIONS_CLOUD_NOT_LOAD_CURRENT_USER) +
					"curent user result: " +
					error.status,
			);
		});
		if (user) {
			this.refresh(user);
			await this._simConfigurationService.reloadConfig();
		}

		if (resetWebSocket) {
			// when the user changes we need to reconnect to the system hub, so users are correctly assigned to the connection
			await this._systemHub.reconnect();
		}
	}

	/**
	 * Used to fetch user before rendering sites
	 */
	public async init() {
		if (!this._user) {
			await this.reload(false);
		}
		return this._user;
	}

	public hasValidToken() {
		return !!this.tokenValidTo && dayjs(this.tokenValidTo).isAfter(new Date());
	}

	/**
	 * Takes an object consisting of a User obj and the accompanying permissions
	 */
	private refresh(userdata: GetCurrentUserResponse): void {
		if (userdata !== null && userdata.user.id !== SimUtilityService.emptyGuid()) {
			this._user = userdata.user;
			this.permissions = userdata.permissions;
			this.tokenValidTo = new Date(userdata.tokenData.validTo);
			this.setTokenTimer();
			this.setUserInitials(this._user);
			this.userChangedEmitter.next({user: this._user});
			this._emitterService.onUserPermChanged.next();
		}
	}

	private timer = null;

	private setTokenTimer() {
		if (!this.timer) {
			this._zone.runOutsideAngular(() => {
				this.timer = setInterval(() => {
					if (this.tokenValidTo) {
						this.tokenValidToMs = dayjs(this.tokenValidTo).diff(dayjs());
						this.tokenValidToFormatted = this.formatSeconds(this.tokenValidToMs);

						//SIMSOFT-6839 Auto-Logout when expired
						if (!!this.user && dayjs(this.tokenValidTo).isBefore(new Date())) {
							this._zone.run(() => {
								this._user = null;
								this._router.navigate(["login"]).then((navigated) => {
									if (!navigated) {
										window.location.reload();
									}
								});
							});
						}
					}
				}, 5000);
			});
		}
	}

	private formatSeconds(durationMs: number): string {
		let duration = durationMs / 1000;
		let hours = Math.floor(duration / 3600);
		let minutes = Math.floor((duration - hours * 3600) / 60);
		let seconds = Math.floor(duration - hours * 3600 - minutes * 60);

		let ret = "";
		if (hours > 24) {
			let days = Math.floor(hours / 24);
			hours = Math.floor(hours - days * 24);
			ret = days + "d ";
		}
		ret +=
			(hours < 10 ? "0" : "") + hours + "h:" + (minutes < 10 ? "0" : "") + minutes + "m:" + (seconds < 10 ? "0" : "") + seconds + "s";
		return ret;
	}

	/**
	 * Returns true if User has the desired Permission
	 *
	 * @param perm {Permissions}
	 * @param overrule
	 * @return {boolean}
	 */
	public hasPermission(perm: Permissions, overrule?: Permissions[]): boolean {
		return this._simPermissionService.HasPerm({permissions: overrule ?? this.permissions}, perm);
	}

	/**
	 * Evaluates User initials from user object
	 *
	 * @param user {@link SimUser}
	 */
	public setUserInitials(user) {
		user.userInitials = "";
		user.userInitials = SimUserExtendedService.getUserInitials(user);
	}

	/**
	 * Evaluates User initials from user object
	 *
	 * @param user {@link SimUser}
	 */
	public static getUserInitials(user: SimUser) {
		let userInitial = "";
		if (user) {
			if (user.firstname) {
				userInitial = user.firstname[0];
			}

			if (user.lastname) {
				userInitial = userInitial + user.lastname[0];
			}

			if (!userInitial && user.username) {
				userInitial = user.username[0];
			}
			userInitial = userInitial.toUpperCase();
		}
		return userInitial;
	}

	public isAnonymous(user: SimUser) {
		return !user || user.id == SimUtilityService.emptyGuid();
	}

	public async logout(automaticReload: boolean = true) {
		await super.logout();
		await this.reload();

		if (automaticReload) {
			const navigated = await this._router.navigate(["login"]);
			if (!navigated) {
				window.location.reload();
			}
		}
	}
}
